import { createTheme } from '@material-ui/core/styles';

import { DESKTOP_FONT_SIZE } from './constants/DESKTOP_FONT_SIZE';

declare module '@material-ui/core/styles/createPalette' {
  interface PaletteColor {
    lighter: string;
    lightest: string;
  }
  interface SimplePaletteColorOptions {
    lighter?: string;
    lightest?: string;
  }

  interface ChartColorOptions {
    cornflowerBlue: string;
    mediumAqua: string;
    pear: string;
    saffron: string;
    corn: string;
    blueGreen: string;
    oxfordBlue: string;
    silverSand: string;
    sandyBrown: string;
  }

  interface Palette {
    chart: ChartColorOptions;
  }

  interface PaletteOptions {
    chart?: ChartColorOptions;
  }
}

export const core = createTheme({
  breakpoints: {
    values: {
      xl: 1440,
      lg: 1024,
      md: 900,
      sm: 600,
      xs: 375,
    },
  },
  palette: {
    primary: {
      light: '#79D8B7',
      lighter: '#ACE4D1',
      lightest: '#E6F7F1',
      main: '#3DBA90',
      dark: '#009974',
    },
    secondary: {
      light: '#668A97',
      lighter: '#76AABE',
      lightest: '#AACAD7',
      main: '#325A69',
      dark: '#11303C',
    },
    error: {
      light: '#F6AFB9',
      lighter: '#FDE5E9',
      main: '#EE415D',
      dark: '#C9354D',
    },
    warning: {
      light: '#F6CD81',
      lighter: '#FBE1B1',
      lightest: '#FEF2DD',
      main: '#F4A91F',
      dark: '#E9A01C',
    },
    info: {
      light: '#BADFEF',
      lighter: '#E4F5FC',
      main: '#43B8EB',
      dark: '#0094D3',
    },
    success: {
      light: '#CEE5B1',
      lighter: '#E7F4D7',
      main: '#88C936',
      dark: '#66B204',
    },
    grey: {
      50: '#FFFFFF',
      100: '#EEEEEE',
      200: '#D2D2D2',
      300: '#BDBDBD',
      400: '#AAAAAA',
      500: '#989898',
      600: '#6B6B6B',
      700: '#454545',
      800: '#3F3F3F',
      900: '#333333',
      A100: '#FAFAFA',
      A200: '#EEEEEE',
      A400: '#E3E3E3',
      A700: '#C5C5C5',
    },
    text: {
      primary: '#454545',
      secondary: '#6B6B6B',
    },
    chart: {
      cornflowerBlue: '#379FED',
      mediumAqua: '#66D6BB',
      pear: '#D3EC3C',
      saffron: '#F1C733',
      corn: '#F0EA61',
      blueGreen: '#30C3CC',
      oxfordBlue: '#091F55',
      silverSand: '#C2C2C2',
      sandyBrown: '#E99C64',
    },
  },
  /**
   * 針對「手機版」的字體設定，可前往 Typography/styles.ts
   */
  typography: {
    fontFamily: [
      'Roboto',
      'Noto Sans TC',
      'PingFang TC',
      '微軟正黑體',
      'sans-serif',
    ].join(','),
    h1: {
      fontSize: `${DESKTOP_FONT_SIZE.h1}rem`,
      fontWeight: 500,
      lineHeight: 1,
      letterSpacing: 0,
      textTransform: 'none',
    },
    h2: {
      fontSize: `${DESKTOP_FONT_SIZE.h2}rem`,
      fontWeight: 500,
      lineHeight: 1,
      letterSpacing: 0,
      textTransform: 'none',
    },
    h3: {
      fontSize: `${DESKTOP_FONT_SIZE.h3}rem`,
      fontWeight: 500,
      lineHeight: 1.5,
      letterSpacing: 0,
      textTransform: 'none',
    },
    h4: {
      fontSize: `${DESKTOP_FONT_SIZE.h4}rem`,
      fontWeight: 500,
      lineHeight: 1.5,
      letterSpacing: 0,
      textTransform: 'none',
    },
    h5: {
      fontSize: `${DESKTOP_FONT_SIZE.h5}rem`,
      fontWeight: 500,
      lineHeight: 1.5,
      letterSpacing: 0,
      textTransform: 'none',
    },
    h6: {
      fontSize: `${DESKTOP_FONT_SIZE.h6}rem`,
      fontWeight: 500,
      lineHeight: 1.5,
      letterSpacing: 0,
      textTransform: 'none',
    },
    subtitle1: {
      fontSize: `${DESKTOP_FONT_SIZE.subtitle1}rem`,
      fontWeight: 500,
      lineHeight: 1.5,
      letterSpacing: 0,
      textTransform: 'none',
    },
    body1: {
      fontSize: `${DESKTOP_FONT_SIZE.body1}rem`,
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: 0,
      textTransform: 'none',
    },
    body2: {
      fontSize: `${DESKTOP_FONT_SIZE.body2}rem`,
      fontWeight: 400,
      lineHeight: 1.5,
      letterSpacing: 0,
      textTransform: 'none',
    },
    button: {
      fontSize: `${DESKTOP_FONT_SIZE.button}rem`,
      fontWeight: 500,
      lineHeight: 1,
      letterSpacing: 0,
      textTransform: 'none',
    },
    caption: {
      fontSize: `${DESKTOP_FONT_SIZE.caption}rem`,
      fontWeight: 500,
      lineHeight: 1.5,
      letterSpacing: 0,
      textTransform: 'none',
    },
    overline: {
      fontSize: `${DESKTOP_FONT_SIZE.overline}rem`,
      fontWeight: 500,
      lineHeight: 1.5,
      letterSpacing: 0,
      textTransform: 'none',
    },
  },
  shadows: [
    'none',
    '0 1px 4px rgba(0, 0, 0, 0.12)',
    '0 4px 12px rgba(0, 0, 0, 0.08)',
    '0 6px 24px rgba(0, 0, 0, 0.1), 0 4px 12px rgba(0, 0, 0, 0.02)',
    '0 8px 48px rgba(0, 0, 0, 0.1), 0 6px 24px rgba(0, 0, 0, 0.04)',
    '0 8px 48px rgba(0, 0, 0, 0.1), 0 6px 24px rgba(0, 0, 0, 0.04)',
    '0 8px 48px rgba(0, 0, 0, 0.1), 0 6px 24px rgba(0, 0, 0, 0.04)',
    '0 8px 48px rgba(0, 0, 0, 0.1), 0 6px 24px rgba(0, 0, 0, 0.04)',
    '0 8px 48px rgba(0, 0, 0, 0.1), 0 6px 24px rgba(0, 0, 0, 0.04)',
    '0 8px 48px rgba(0, 0, 0, 0.1), 0 6px 24px rgba(0, 0, 0, 0.04)',
    '0 8px 48px rgba(0, 0, 0, 0.1), 0 6px 24px rgba(0, 0, 0, 0.04)',
    '0 8px 48px rgba(0, 0, 0, 0.1), 0 6px 24px rgba(0, 0, 0, 0.04)',
    '0 8px 48px rgba(0, 0, 0, 0.1), 0 6px 24px rgba(0, 0, 0, 0.04)',
    '0 8px 48px rgba(0, 0, 0, 0.1), 0 6px 24px rgba(0, 0, 0, 0.04)',
    '0 8px 48px rgba(0, 0, 0, 0.1), 0 6px 24px rgba(0, 0, 0, 0.04)',
    '0 8px 48px rgba(0, 0, 0, 0.1), 0 6px 24px rgba(0, 0, 0, 0.04)',
    '0 8px 48px rgba(0, 0, 0, 0.1), 0 6px 24px rgba(0, 0, 0, 0.04)',
    '0 8px 48px rgba(0, 0, 0, 0.1), 0 6px 24px rgba(0, 0, 0, 0.04)',
    '0 6px 24px rgba(0, 0, 0, 0.1), 0 4px 12px rgba(0, 0, 0, 0.02)',
    '0 8px 48px rgba(0, 0, 0, 0.1), 0 6px 24px rgba(0, 0, 0, 0.04)',
    '0 8px 48px rgba(0, 0, 0, 0.1), 0 6px 24px rgba(0, 0, 0, 0.04)',
    '0 8px 48px rgba(0, 0, 0, 0.1), 0 6px 24px rgba(0, 0, 0, 0.04)',
    '0 8px 48px rgba(0, 0, 0, 0.1), 0 6px 24px rgba(0, 0, 0, 0.04)',
    '0 8px 48px rgba(0, 0, 0, 0.1), 0 6px 24px rgba(0, 0, 0, 0.04)',
    '0 8px 48px rgba(0, 0, 0, 0.1), 0 6px 24px rgba(0, 0, 0, 0.04)',
  ]
});
